/**
 * Auswahl und Vorbelegung des Storefront-Filters in den Vario-Fenstern.
 *
 * Beide Tabs, die diese Funktionen nutzen (Angebote, Bestellungen), haengen im Vario-Slot
 * `settings.sales-channels.{appId}.tabs` — der Haendler oeffnet sie also immer aus einem
 * konkreten Verkaufskanal heraus. Die Kanal-ID kommt per URL an (siehe `vario-route-context`),
 * die Zuordnung Kanal → Storefront liefert `kaufland_list_storefronts`.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

export interface ActivatedStorefront {
  storefront: string;
  varioChannelId: string | null;
}

/**
 * Aktivierte Storefronts des Mandanten samt zugehoerigem Vario-Verkaufskanal.
 *
 * Ersetzt zwei zeichengleiche Kopien von `listActiveStorefronts`, die in `offers.ts` und
 * `orders.ts` nebeneinander standen und die `vario_channel_id` beide wegwarfen, obwohl der RPC
 * sie mitliefert. Fehler werden geschluckt und als leere Liste gemeldet: der Storefront-Filter
 * ist Beiwerk und darf einen Tab nicht scheitern lassen.
 */
export async function loadActivatedStorefronts(supabase: SupabaseClient): Promise<ActivatedStorefront[]> {
  const { data, error } = await supabase.rpc('kaufland_list_storefronts');
  if (error || !data) {
    if (error) console.error('[storefront-selection] kaufland_list_storefronts', error);
    return [];
  }
  return (data as Array<{ storefront: string; vario_channel_id: string | null }>).map((r) => ({
    storefront: r.storefront,
    varioChannelId: r.vario_channel_id ?? null,
  }));
}

/** Storefront, der beim Oeffnen vorbelegt wird. `null` heisst „Alle Storefronts". */
export function resolveInitialStorefront(
  rows: ActivatedStorefront[],
  channelId: string | null,
): string | null {
  if (channelId) {
    const hit = rows.find((r) => r.varioChannelId === channelId);
    // Der Kanal-Treffer schlaegt DE. Waere es umgekehrt, brauchte man die Vorbelegung nicht.
    if (hit) return hit.storefront;
  }
  if (rows.some((r) => r.storefront === 'de')) return 'de';
  // Bewusst kein „irgendein aktivierter Storefront": ein Mandant ohne DE landet auf der
  // Gesamtuebersicht, nicht auf einem willkuerlichen Land.
  return null;
}

/**
 * Werte des Storefront-Auswahlfelds: aktivierte Storefronts vereinigt mit denen, die in den
 * geladenen Daten vorkommen.
 *
 * Beide Haelften sind noetig. Ohne die aktivierten fehlt ein frisch freigeschalteter Marktplatz,
 * solange er keine Zeile hat — genau so verschwand NL nach der Aktivierung am 2026-08-09 aus der
 * Angebots-Auswahl. Ohne die aus den Daten waeren Zeilen eines nicht mehr aktivierten
 * Storefronts sichtbar, aber nicht filterbar.
 */
export function storefrontFilterOptions(
  rows: ActivatedStorefront[],
  fromData: string[],
): string[] {
  return [...new Set([...rows.map((r) => r.storefront), ...fromData])].sort();
}
